import React from "react";
import { Button, Modal, Table } from "react-bootstrap";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  Row as TanRow,
  useReactTable,
} from "@tanstack/react-table";
import ReportTableFrame from "@core/components/ReportTableFrame";
import {
  columnHasViewableText,
  formatBooleanValue,
  formatGridRollupValue,
  getBooleanValue,
  getAggregatedNumericValue,
  getGridDisplayMeta,
  getInlineBooleanCellText,
  getNumericValue,
  getViewableValueText,
  gridMetaFromRollup,
  isGridRollupValue,
  isGridRowMetaKey,
  truncateViewableText,
} from "@core/reports/utils/reportCellValues";
export type ReportMetricRow = {
  id: string;
  name: string;
  [key: string]: unknown;
  subRows?: ReportMetricRow[];
};

export type ReportColumnLeaf = {
  id: string; // unique column id
  header: string; // column header label
  field: string; // row[field] used to fetch values
};

export type ReportColumnGroup = {
  header: string; // group header label
  columns: ReportColumnLeaf[]; // children columns
};

export type ReportColumnsSpec = {
  nameHeader?: string; // left column header, default ""
  groups: ReportColumnGroup[];
};

type Props = {
  title?: React.ReactNode;
  /** Left-side frame header slot (e.g. section View controls). */
  toolbarStart?: React.ReactNode;
  data: ReportMetricRow[];
  columnsSpec: ReportColumnsSpec;
  defaultExpanded?: boolean;

  // Render ONLY the label content. The table will still render the expander + indentation.
  renderName?: (row: TanRow<ReportMetricRow>) => React.ReactNode;

  totals?: Record<string, unknown> | null;
  totalsLabel?: string;
};

type ViewableGridMeta = {
  rows: Array<Record<string, string>>;
  columnLabels: Record<string, string>;
  columnOrder: string[];
  emphasizeLastRow?: boolean;
};

type ViewableCellEntry = {
  rowLabel: string;
  content: string;
  grid?: ViewableGridMeta | null;
};

type ViewerState = {
  rowLabel: string;
  columnLabel: string;
  entries: ViewableCellEntry[];
  /** Optional in-cell preview override (e.g. full grid_rollup text while modal shows table). */
  previewContent?: string;
  /** Force Read more (e.g. grid_rollup always offers the merged table). */
  forceReadMore?: boolean;
};

type ViewerPayload = ViewerState;

function getAggregatedValue(row: ReportMetricRow, field: string): number | null {
  return getAggregatedNumericValue(row, field);
}

// For normal (non-totals) cells: hide 0 so the table is cleaner
function formatNumber(n: number | null) {
  if (n === null) return "";
  return n.toLocaleString();
}

// For totals row: show 0 (totals should be explicit)
function formatTotalsValue(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (isGridRollupValue(v)) return formatGridRollupValue(v) ?? "";
  const booleanValue = getBooleanValue(v);
  if (booleanValue !== null) return formatBooleanValue(booleanValue);
  const numericValue = getNumericValue(v);
  if (numericValue !== null) return numericValue.toLocaleString();
  return typeof v === "string" ? v : "";
}

function getAllLeafFields(columnsSpec: ReportColumnsSpec): ReportColumnLeaf[] {
  return columnsSpec.groups.flatMap((g) => g.columns);
}

function getRowLabel(row: ReportMetricRow): string {
  const label = String(row.name ?? "").trim();
  return label || "Entry";
}

function orderedGridKeys(grid: ViewableGridMeta): string[] {
  const keys: string[] = [];
  for (const key of grid.columnOrder) {
    if (isGridRowMetaKey(key) || keys.includes(key)) continue;
    keys.push(key);
  }
  for (const row of grid.rows) {
    for (const key of Object.keys(row)) {
      if (isGridRowMetaKey(key) || keys.includes(key)) continue;
      keys.push(key);
    }
  }
  for (const key of Object.keys(grid.columnLabels)) {
    if (isGridRowMetaKey(key) || keys.includes(key)) continue;
    keys.push(key);
  }
  return keys;
}

function collectViewableEntries(row: ReportMetricRow, field: string): ViewableCellEntry[] {
  const entries: ViewableCellEntry[] = [];
  const raw = row?.[field];

  // Parent/district grid_rollup is authoritative — do not walk child grids.
  if (isGridRollupValue(raw)) {
    const content = formatGridRollupValue(raw) ?? "";
    if (content) {
      entries.push({
        rowLabel: getRowLabel(row),
        content,
      });
    }
    return entries;
  }

  const content = getViewableValueText(raw);
  const grid = getGridDisplayMeta(raw);

  if (content || grid) {
    entries.push({
      rowLabel: getRowLabel(row),
      content: content ?? "",
      grid,
    });
  }

  for (const child of row.subRows ?? []) {
    entries.push(...collectViewableEntries(child, field));
  }

  return entries;
}

function buildViewerPayload(row: ReportMetricRow, field: string, columnLabel: string): ViewerPayload | null {
  const raw = row?.[field];
  if (isGridRollupValue(raw)) {
    return buildGridRollupViewerPayload(getRowLabel(row), columnLabel, raw);
  }

  const entries = collectViewableEntries(row, field);
  if (entries.length === 0) return null;

  return {
    rowLabel: getRowLabel(row),
    columnLabel,
    entries,
  };
}

function buildGridRollupViewerPayload(
  rowLabel: string,
  columnLabel: string,
  value: unknown
): ViewerPayload | null {
  const content = formatGridRollupValue(value) ?? "";
  if (!content) return null;

  const grid = gridMetaFromRollup(value);
  if (!grid) return null;

  return {
    rowLabel,
    columnLabel,
    entries: [{ rowLabel, content, grid }],
    previewContent: content,
    forceReadMore: true,
  };
}

function buildValueViewerPayload(rowLabel: string, columnLabel: string, value: unknown): ViewerPayload | null {
  if (getBooleanValue(value) !== null) return null;

  if (isGridRollupValue(value)) {
    return buildGridRollupViewerPayload(rowLabel, columnLabel, value);
  }

  const content = getViewableValueText(value);
  const grid = getGridDisplayMeta(value);
  if (!content && !grid) return null;

  return {
    rowLabel,
    columnLabel,
    entries: [{ rowLabel, content: content ?? "", grid }],
  };
}

function readMoreAriaLabel(payload: ViewerPayload): string {
  const countSuffix = payload.entries.length > 1 ? `, ${payload.entries.length} entries` : "";
  return `Read more, ${payload.columnLabel}, ${payload.rowLabel}${countSuffix}`;
}

function ReadMoreButton({
  payload,
  onOpen,
}: {
  payload: ViewerPayload;
  onOpen: (state: ViewerState) => void;
}) {
  return (
    <Button
      variant="link"
      size="sm"
      className="p-0 fw-semibold text-decoration-none"
      aria-label={readMoreAriaLabel(payload)}
      onClick={() =>
        onOpen({
          rowLabel: payload.rowLabel,
          columnLabel: payload.columnLabel,
          entries: payload.entries,
          previewContent: payload.previewContent,
          forceReadMore: payload.forceReadMore,
        })
      }
    >
      Read more
    </Button>
  );
}

function ViewableGridTable({ grid }: { grid: ViewableGridMeta }) {
  const keys = orderedGridKeys(grid);
  const lastIndex = grid.rows.length - 1;
  return (
    <div className="table-responsive">
      <Table bordered size="sm" className="mb-0 align-middle">
        <thead className="table-light">
          <tr>
            {keys.map((key) => (
              <th key={key}>{grid.columnLabels[key]?.trim() || key}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.rows.map((row, rowIndex) => (
            <tr
              key={`grid-row-${rowIndex}`}
              className={
                grid.emphasizeLastRow && rowIndex === lastIndex
                  ? "table-light fw-semibold"
                  : undefined
              }
            >
              {keys.map((key) => (
                <td key={key}>{row[key] ?? ""}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function ViewableEntryBody({ entry }: { entry: ViewableCellEntry }) {
  if (entry.grid) {
    return <ViewableGridTable grid={entry.grid} />;
  }
  return (
    <div
      style={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        lineHeight: 1.5,
      }}
    >
      {entry.content}
    </div>
  );
}

function ViewableTextCell({
  payload,
  onOpen,
}: {
  payload: ViewerPayload;
  onOpen: (state: ViewerState) => void;
}) {
  const multiEntry = payload.entries.length > 1;
  const first = payload.entries[0];
  const previewSource = payload.previewContent ?? first.content;
  const preview = truncateViewableText(previewSource);
  const multiRowGrid = (first.grid?.rows.length ?? 0) > 1;
  const showReadMore =
    preview.isTruncated || multiEntry || multiRowGrid || Boolean(payload.forceReadMore);
  const previewText =
    showReadMore && !preview.isTruncated ? `${preview.text}…` : preview.text;
  // When previewContent is set (grid_rollup), don't prefix the first entry row label.
  const showEntryLabel = multiEntry && !payload.previewContent;

  return (
    <div className="ntcog-rt-text-cell">
      {showEntryLabel ? <span className="fw-semibold">{first.rowLabel}</span> : null}
      {showEntryLabel ? " " : null}
      {previewText}
      {showReadMore ? (
        <>
          {" "}
          <ReadMoreButton payload={payload} onOpen={onOpen} />
        </>
      ) : null}
    </div>
  );
}

function someRowHasChildren(rows: ReportMetricRow[]): boolean {
  return rows.some((row) => (row.subRows?.length ?? 0) > 0);
}

export default function ReportTreeTable({
  title,
  toolbarStart,
  data,
  columnsSpec,
  defaultExpanded = false,
  renderName,
  totals = null,
  totalsLabel = "TOTAL",
}: Props) {
  const [viewerState, setViewerState] = React.useState<ViewerState | null>(null);
  const [scrolled, setScrolled] = React.useState(false);
  const [headerRow0Height, setHeaderRow0Height] = React.useState(0);
  const tableWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const scrollBoxRef = React.useRef<HTMLDivElement | null>(null);
  const firstHeaderRowRef = React.useRef<HTMLTableRowElement | null>(null);
  const boundaryHeaderRefs = React.useRef<Record<number, HTMLTableCellElement | null>>({});
  const [groupBoundaryOffsets, setGroupBoundaryOffsets] = React.useState<number[]>([]);

  const textFields = React.useMemo(() => {
    const fields = new Set<string>();
    for (const leaf of getAllLeafFields(columnsSpec)) {
      if (columnHasViewableText(data, leaf.field)) fields.add(leaf.id);
    }
    return fields;
  }, [columnsSpec, data]);

  const columns = React.useMemo<ColumnDef<ReportMetricRow>[]>(() => {
    const nameCol: ColumnDef<ReportMetricRow> = {
      id: "name",
      header: columnsSpec.nameHeader ?? "",
      cell: ({ row }) => {
        const canExpand = row.getCanExpand();
        const isExpanded = row.getIsExpanded();

        const label = renderName ? (
          renderName(row)
        ) : (
          <span
            style={{
              fontWeight: row.depth === 0 ? 800 : row.depth === 1 ? 700 : 600,
              whiteSpace: "normal",
              overflowWrap: "break-word",
              wordBreak: "break-word",
              lineHeight: 1.25,
              display: "inline-block",
            }}
            title={row.original.name}
          >
            {row.original.name}
          </span>
        );

        return (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, minWidth: 0 }}>
            {canExpand ? (
              <button
                type="button"
                onClick={row.getToggleExpandedHandler()}
                aria-label={isExpanded ? "Collapse row" : "Expand row"}
                aria-expanded={isExpanded}
                className="ntcog-rt-expander"
              >
                {isExpanded ? "▾" : "▸"}
              </button>
            ) : (
              <span style={{ width: 28, display: "inline-block", flex: "0 0 auto" }} />
            )}

            <div style={{ paddingLeft: row.depth === 0 ? 0 : 18, flex: "1 1 auto", minWidth: 0 }}>
              {label}
            </div>
          </div>
        );
      },
    };

    const groupedCols: ColumnDef<ReportMetricRow>[] = columnsSpec.groups.map((g, gi) => ({
      // TanStack derives a group's id from its string header; an empty/falsy header
      // (e.g. the flat single-group pastor spec) can't derive one, so supply a stable id.
      ...(typeof g.header === "string" && g.header.trim() ? {} : { id: `group_${gi}` }),
      header: g.header,
      columns: g.columns.map((leaf) => ({
        id: leaf.id,
        header: () => (
          <div
            style={{
              whiteSpace: "normal",
              wordBreak: "keep-all",
              overflowWrap: "normal",
              hyphens: "none",
              lineHeight: 1.15,
              textAlign: "center",
              maxWidth: 220,
              margin: "0 auto",
            }}
            title={leaf.header}
          >
            {leaf.header}
          </div>
        ),
        cell: ({ row }: { row: TanRow<ReportMetricRow> }) => {
          const booleanText = getInlineBooleanCellText(row.original, leaf.field);
          if (booleanText !== null) {
            return booleanText;
          }

          const viewerPayload = buildViewerPayload(row.original, leaf.field, leaf.header);

          if (viewerPayload) {
            return <ViewableTextCell payload={viewerPayload} onOpen={setViewerState} />;
          }

          return formatNumber(
            (row.original.subRows?.length ?? 0) > 0
              ? getAggregatedValue(row.original, leaf.field)
              : getNumericValue(row.original?.[leaf.field])
          );
        },
        meta: { isText: textFields.has(leaf.id) },
      })),
    }));

    return [nameCol, ...groupedCols];
  }, [columnsSpec, renderName, textFields]);

  const table = useReactTable({
    data,
    columns,
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    initialState: { expanded: defaultExpanded ? true : {} },
  });

  const leafFields = React.useMemo(() => getAllLeafFields(columnsSpec), [columnsSpec]);
  const canExpandSome = someRowHasChildren(data);

  // Column indices (1..N) where a new activity group starts.
  // Drawing the strong divider on the left edge of each group keeps the line
  // visually continuous from the top header row down through the table body.
  const groupStartColumnIndices = React.useMemo(() => {
    const set = new Set<number>();
    let colIdx = 1; // first data column index
    for (const g of columnsSpec.groups) {
      set.add(colIdx);
      colIdx += g.columns.length;
    }
    return set;
  }, [columnsSpec.groups]);

  const setBoundaryHeaderRef = React.useCallback(
    (cellColumnIndex: number, node: HTMLTableCellElement | null) => {
      if (node) {
        boundaryHeaderRefs.current[cellColumnIndex] = node;
        return;
      }

      delete boundaryHeaderRefs.current[cellColumnIndex];
    },
    []
  );

  const measureGroupBoundaryOffsets = React.useCallback(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const nextOffsets = Array.from(groupStartColumnIndices)
      .sort((a, b) => a - b)
      .map((cellColumnIndex) => {
        const cell = boundaryHeaderRefs.current[cellColumnIndex];
        if (!cell) return null;
        return cell.getBoundingClientRect().left - wrapperRect.left;
      })
      .filter((offset): offset is number => offset !== null);

    setGroupBoundaryOffsets((currentOffsets) => {
      if (
        currentOffsets.length === nextOffsets.length &&
        currentOffsets.every((offset, idx) => Math.abs(offset - nextOffsets[idx]) < 0.5)
      ) {
        return currentOffsets;
      }

      return nextOffsets;
    });
  }, [groupStartColumnIndices]);

  const measureHeaderRow0 = React.useCallback(() => {
    const row = firstHeaderRowRef.current;
    if (!row) return;
    const nextHeight = row.getBoundingClientRect().height;
    setHeaderRow0Height((current) => (Math.abs(current - nextHeight) < 0.5 ? current : nextHeight));
  }, []);

  React.useLayoutEffect(() => {
    const wrapper = tableWrapperRef.current;
    const box = scrollBoxRef.current;
    if (!wrapper) return undefined;

    const measure = () => {
      measureGroupBoundaryOffsets();
      measureHeaderRow0();
    };

    const frameId = window.requestAnimationFrame(measure);
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            measure();
          })
        : null;

    resizeObserver?.observe(wrapper);
    if (box) resizeObserver?.observe(box);
    Object.values(boundaryHeaderRefs.current).forEach((cell) => {
      if (cell) resizeObserver?.observe(cell);
    });
    if (firstHeaderRowRef.current) resizeObserver?.observe(firstHeaderRowRef.current);

    window.addEventListener("resize", measure);

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measureGroupBoundaryOffsets, measureHeaderRow0]);

  React.useEffect(() => {
    const box = scrollBoxRef.current;
    if (!box) return undefined;

    const onScroll = () => {
      setScrolled(box.scrollLeft > 0);
    };
    onScroll();
    box.addEventListener("scroll", onScroll, { passive: true });
    return () => box.removeEventListener("scroll", onScroll);
  }, []);

  const overlayOffsets = groupBoundaryOffsets.slice(1);

  return (
    <>
      <ReportTableFrame
        title={title}
        toolbarStart={toolbarStart}
        freeze
        scrolled={scrolled}
        boxRef={scrollBoxRef}
        style={
          headerRow0Height > 0
            ? ({ "--ntcog-rt-header-row0-height": `${headerRow0Height}px` } as React.CSSProperties)
            : undefined
        }
        toolbar={
          canExpandSome ? (
            <div className="ntcog-rt-toolbar">
              <Button
                variant="link"
                size="sm"
                className="p-0 text-decoration-none"
                onClick={() => table.toggleAllRowsExpanded(true)}
              >
                Expand all
              </Button>
              <Button
                variant="link"
                size="sm"
                className="p-0 text-decoration-none"
                onClick={() => table.toggleAllRowsExpanded(false)}
              >
                Collapse all
              </Button>
            </div>
          ) : null
        }
      >
        <div
          ref={tableWrapperRef}
          style={{ position: "relative", display: "inline-block", minWidth: "100%" }}
        >
          {overlayOffsets.length > 0 ? (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                zIndex: 2,
              }}
            >
              {overlayOffsets.map((offset, idx) => (
                <div
                  key={`group-divider-${idx}`}
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: offset,
                    width: 5,
                    transform: "translateX(-2.5px)",
                    background: "var(--ntcog-border-strong)",
                  }}
                />
              ))}
            </div>
          ) : null}

          <table className="ntcog-report-table" style={{ minWidth: 900 }}>
            <thead>
              {table.getHeaderGroups().map((hg, groupIndex) => (
                <tr key={hg.id} ref={groupIndex === 0 ? firstHeaderRowRef : undefined}>
                  {hg.headers.map((header) => {
                    const isGroupHeader = header.depth === 0 && header.colSpan > 1;
                    const isText = Boolean(
                      (header.column.columnDef.meta as { isText?: boolean } | undefined)?.isText
                    );

                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        scope="col"
                        className={[isGroupHeader ? "is-group" : undefined, isText ? "is-text" : undefined]
                          .filter(Boolean)
                          .join(" ") || undefined}
                        ref={
                          header.depth === 1 && groupStartColumnIndices.has(header.index)
                            ? (node: HTMLTableCellElement | null) =>
                                setBoundaryHeaderRef(header.index, node)
                            : undefined
                        }
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} data-depth={row.depth}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={textFields.has(cell.column.id) ? "is-text" : undefined}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>

            {totals ? (
              <tfoot>
                <tr>
                  <td>{String((totals as { template_name?: unknown }).template_name ?? totalsLabel)}</td>

                  {leafFields.map((leaf) => {
                    const value = (totals as Record<string, unknown>)[leaf.field];
                    const viewerPayload = buildValueViewerPayload(
                      String((totals as { template_name?: unknown }).template_name ?? totalsLabel),
                      leaf.header,
                      value
                    );
                    return (
                      <td
                        key={`totals_${leaf.id}`}
                        className={textFields.has(leaf.id) ? "is-text" : undefined}
                      >
                        {viewerPayload ? (
                          <ViewableTextCell payload={viewerPayload} onOpen={setViewerState} />
                        ) : textFields.has(leaf.id) ? (
                          "-"
                        ) : (
                          formatTotalsValue(value)
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </ReportTableFrame>

      <Modal
        show={!!viewerState}
        onHide={() => setViewerState(null)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{viewerState?.columnLabel ?? "Details"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-2">
            {viewerState?.rowLabel ?? "Entry"}
          </div>
          {viewerState && viewerState.entries.length > 1 ? (
            viewerState.entries.map((entry, index) => (
              <div key={`${entry.rowLabel}-${index}`}>
                {index > 0 ? <hr /> : null}
                <div className="fw-semibold mb-2">{entry.rowLabel}</div>
                <ViewableEntryBody entry={entry} />
              </div>
            ))
          ) : viewerState?.entries[0] ? (
            <ViewableEntryBody entry={viewerState.entries[0]} />
          ) : null}
        </Modal.Body>
      </Modal>
    </>
  );
}
